import { useState, useEffect, useCallback } from 'react';
import { AlertTriangle, Check, MapPin, Plus, Star, Truck, X } from 'lucide-react';
import { api } from '../../lib/api';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import type { Truck as TruckType } from '../../types/admin';
import type { AssignedTruck } from '../../hooks/useJobs';

// ── Types ─────────────────────────────────────────────────────────────────────

interface SuggestedTruck {
  truckId: string;
  truckName: string;
  truckType: string;
  isExactTypeMatch: boolean;
  isUpgrade: boolean;
  expectedWarehouseName: string | null;
  distanceToOriginMeters: number | null;
}

interface TruckRecommendation {
  recommendedType: string;
  recommendedCount: number;
  suggestedTrucks: SuggestedTruck[];
}

interface RecommendedTrucksResponse {
  success: boolean;
  data: { recommendations: TruckRecommendation[] };
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatDistance(meters: number | null): string | null {
  if (meters == null) return null;
  const miles = meters / 1609.344;
  return miles < 10 ? `${miles.toFixed(1)} mi` : `${Math.round(miles)} mi`;
}

function formatTruckType(type: string): string {
  return type.replace(/_/g, ' ');
}

// ── Component ─────────────────────────────────────────────────────────────────

interface TruckAssignmentModalProps {
  orgId: string;
  jobId: string;
  moveDate: string | undefined;
  trucksNeeded: number;
  currentTrucks: AssignedTruck[];
  onSave: (trucks: AssignedTruck[]) => Promise<void>;
  onClose: () => void;
}

export function TruckAssignmentModal({
  orgId,
  jobId,
  moveDate,
  trucksNeeded,
  currentTrucks,
  onSave,
  onClose,
}: TruckAssignmentModalProps) {
  const [assigned, setAssigned] = useState<AssignedTruck[]>(currentTrucks);
  const [allTrucks, setAllTrucks] = useState<TruckType[]>([]);
  const [busyTruckIds, setBusyTruckIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [recommendations, setRecommendations] = useState<TruckRecommendation[]>([]);
  const [recsLoading, setRecsLoading] = useState(false);
  const [upgradeTooltip, setUpgradeTooltip] = useState<string | null>(null);

  const truckCount = assigned.length;
  const allSlotsFilled = truckCount >= trucksNeeded && trucksNeeded > 0;

  // Fetch trucks + availability
  useEffect(() => {
    if (!orgId) return;
    setLoading(true);

    const trucksPromise = api.get<{ success: boolean; data: TruckType[] }>(`/fleet/${orgId}/trucks`);
    const availPromise = moveDate
      ? api.get<{ success: boolean; data: { busyCrewUserIds: string[]; busyTruckIds: string[] } }>(
          `/jobs/${orgId}/availability?date=${moveDate}&excludeJobId=${jobId}`,
        )
      : Promise.resolve({ data: { busyCrewUserIds: [] as string[], busyTruckIds: [] as string[] } });

    Promise.all([trucksPromise, availPromise])
      .then(([trucksRes, availRes]) => {
        setAllTrucks(trucksRes.data);
        setBusyTruckIds(new Set(availRes.data.busyTruckIds));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [orgId, moveDate, jobId]);

  // Fetch recommended trucks (separate call, non-blocking)
  useEffect(() => {
    if (!orgId || !jobId) return;
    setRecsLoading(true);
    api.get<RecommendedTrucksResponse>(`/jobs/${orgId}/${jobId}/recommended-trucks`)
      .then(res => {
        if (res.data?.recommendations) {
          setRecommendations(res.data.recommendations);
        }
      })
      .catch(() => {})
      .finally(() => setRecsLoading(false));
  }, [orgId, jobId]);

  // Auto-close when all slots filled
  useEffect(() => {
    if (allSlotsFilled && truckCount > currentTrucks.length) {
      handleSave();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allSlotsFilled, truckCount]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      await onSave(assigned);
      onClose();
    } catch {
      setSaving(false);
    }
  }, [assigned, onSave, onClose]);

  function assignTruck(truck: TruckType) {
    if (assigned.some(t => t.truckId === truck.id)) return;
    if (truckCount >= trucksNeeded && trucksNeeded > 0) return;
    setAssigned(prev => [...prev, { truckId: truck.id, truckName: truck.name }]);
  }

  function assignTruckById(truckId: string, truckName: string) {
    if (assigned.some(t => t.truckId === truckId)) return;
    if (truckCount >= trucksNeeded && trucksNeeded > 0) return;
    setAssigned(prev => [...prev, { truckId, truckName }]);
  }

  function removeTruck(index: number) {
    setAssigned(prev => prev.filter((_, i) => i !== index));
  }

  const assignedTruckIds = new Set(assigned.map(t => t.truckId));

  // Collect all recommended truck IDs for exclusion from Available section
  const recommendedTruckIds = new Set<string>();
  for (const rec of recommendations) {
    for (const s of rec.suggestedTrucks) {
      recommendedTruckIds.add(s.truckId);
    }
  }

  const availableTrucks = allTrucks.filter(t =>
    t.status === 'active' &&
    !assignedTruckIds.has(t.id) &&
    !busyTruckIds.has(t.id) &&
    !recommendedTruckIds.has(t.id),
  );
  const busyTrucks = allTrucks.filter(t =>
    t.status === 'active' &&
    !assignedTruckIds.has(t.id) &&
    busyTruckIds.has(t.id),
  );

  // Filter out assigned trucks from recommendations for display,
  // and limit total suggestions to remaining slots needed
  const slotsRemaining = Math.max(0, trucksNeeded - truckCount);
  const visibleRecommendations: TruckRecommendation[] = [];
  let suggestionsShown = 0;
  for (const rec of recommendations) {
    if (suggestionsShown >= slotsRemaining) break;
    const filtered = rec.suggestedTrucks.filter(s => !assignedTruckIds.has(s.truckId));
    const maxForThisRec = Math.min(rec.recommendedCount, slotsRemaining - suggestionsShown);
    const limited = filtered.slice(0, maxForThisRec);
    if (limited.length > 0) {
      visibleRecommendations.push({ ...rec, suggestedTrucks: limited });
      suggestionsShown += limited.length;
    }
  }

  const hasRecommendations = visibleRecommendations.length > 0;

  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end md:items-center md:justify-center">
      <div className="absolute inset-0 bg-midnight/50" onClick={onClose} aria-hidden="true" />
      <div className="relative bg-white rounded-t-2xl px-4 pt-5 pb-8 md:rounded-2xl md:max-w-md md:w-full md:pb-6 max-h-[85vh] flex flex-col">
        <div className="md:hidden w-10 h-1 bg-stone rounded-full mx-auto mb-5" />

        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="font-display text-h3 text-midnight">Assign Trucks</h2>
            <p className="text-[13px] text-slate mt-1">
              <span className="font-semibold text-midnight">{truckCount}</span> / {trucksNeeded} trucks assigned
            </p>
          </div>
          <button type="button" onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-slate hover:text-midnight hover:bg-mist transition-colors shrink-0"
            aria-label="Close"><X size={18} /></button>
        </div>

        {/* Required truck types */}
        {recommendations.length > 0 && (
          <div className="mb-4 px-3 py-2.5 rounded-xl bg-mist border border-stone/60">
            <p className="text-[11px] font-bold uppercase tracking-widest text-slate mb-1.5">Required</p>
            <div className="flex flex-wrap gap-2">
              {recommendations.map(rec => (
                <span key={rec.recommendedType}
                  className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg bg-white border border-stone text-[12px] font-medium text-midnight shadow-sm">
                  <Truck size={11} className="text-plum" />
                  {rec.recommendedCount}× {formatTruckType(rec.recommendedType)}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Assigned trucks */}
        {assigned.length > 0 && (
          <div className="mb-4">
            <p className="text-[11px] font-bold uppercase tracking-widest text-slate mb-2">Assigned</p>
            <ul className="flex flex-col gap-1.5">
              {assigned.map((truck, i) => (
                <li key={truck.truckId}
                  className="flex items-center gap-2 px-3 py-2 rounded-xl border border-blue-200 bg-blue-50">
                  <div className="w-7 h-7 rounded-lg bg-blue-100 flex items-center justify-center shrink-0">
                    <Truck size={13} className="text-blue-600" />
                  </div>
                  <p className="flex-1 text-[13px] font-medium text-midnight truncate">{truck.truckName}</p>
                  <button type="button" onClick={() => removeTruck(i)}
                    className="w-7 h-7 flex items-center justify-center rounded-lg text-slate hover:text-rose hover:bg-rose/10 transition-colors"
                    aria-label="Remove">
                    <X size={13} />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Scrollable truck lists */}
        <div className="flex-1 overflow-y-auto -mx-1 px-1 min-h-0">
          {loading ? (
            <div className="flex justify-center py-8">
              <LoadingSpinner size="md" />
            </div>
          ) : (
            <>
              {!allSlotsFilled && (
                <>
                  {/* ── Recommended trucks ── */}
                  {recsLoading && (
                    <div className="mb-3">
                      <p className="text-[11px] font-bold uppercase tracking-widest text-slate mb-2">Recommended</p>
                      <div className="flex justify-center py-3">
                        <LoadingSpinner size="sm" />
                      </div>
                    </div>
                  )}

                  {!recsLoading && hasRecommendations && (
                    <div className="mb-3">
                      <p className="text-[11px] font-bold uppercase tracking-widest text-slate mb-2">Recommended</p>
                      <ul className="flex flex-col gap-1">
                        {visibleRecommendations.map(rec => (
                          rec.suggestedTrucks.map(s => (
                            <li key={s.truckId}>
                              <button
                                type="button"
                                onClick={() => assignTruckById(s.truckId, s.truckName)}
                                className="w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-xl border border-emerald-200
                                           bg-emerald-50/50 hover:border-emerald-400 hover:bg-emerald-50 active:bg-emerald-100
                                           transition-all duration-150"
                              >
                                <div className="w-7 h-7 rounded-lg bg-emerald-100 flex items-center justify-center shrink-0">
                                  <Star size={13} className="text-emerald-600" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-1.5">
                                    <p className="text-[13px] font-medium text-midnight truncate">{s.truckName}</p>
                                    {s.isUpgrade && (
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setUpgradeTooltip(prev => prev === s.truckId ? null : s.truckId);
                                        }}
                                        className="shrink-0 w-5 h-5 flex items-center justify-center"
                                        aria-label="Upgrade warning"
                                      >
                                        <AlertTriangle size={12} className="text-amber-500" />
                                      </button>
                                    )}
                                  </div>
                                  <p className="text-[11px] text-slate capitalize">
                                    {formatTruckType(s.truckType)}
                                    {!s.isExactTypeMatch && (
                                      <span className="text-[10px] text-slate/70"> (rec: {formatTruckType(rec.recommendedType)})</span>
                                    )}
                                  </p>
                                  {s.distanceToOriginMeters != null && (
                                    <p className="text-[10px] text-slate/70 flex items-center gap-0.5 mt-0.5">
                                      <MapPin size={9} />
                                      {formatDistance(s.distanceToOriginMeters)} from origin
                                      {s.expectedWarehouseName && ` \u00B7 ${s.expectedWarehouseName}`}
                                    </p>
                                  )}
                                  {upgradeTooltip === s.truckId && (
                                    <p className="text-[10px] text-amber-700 bg-amber-50 rounded px-1.5 py-1 mt-1">
                                      This truck is larger than recommended — it may be an inefficient use of the truck.
                                    </p>
                                  )}
                                </div>
                                <Plus size={14} className="text-emerald-600 shrink-0" />
                              </button>
                            </li>
                          ))
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* ── Available trucks ── */}
                  {availableTrucks.length > 0 && (
                    <div className="mb-3">
                      <p className="text-[11px] font-bold uppercase tracking-widest text-slate mb-2">Available</p>
                      <ul className="flex flex-col gap-1">
                        {availableTrucks.map(t => (
                          <li key={t.id}>
                            <button type="button" onClick={() => assignTruck(t)}
                              className="w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-xl border border-stone
                                         hover:border-blue-300 hover:bg-blue-50/50 active:bg-mist transition-all duration-150">
                              <div className="w-7 h-7 rounded-lg bg-stone/60 flex items-center justify-center shrink-0">
                                <Truck size={13} className="text-slate" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-[13px] font-medium text-midnight truncate">{t.name}</p>
                                {t.type && (
                                  <p className="text-[11px] text-slate capitalize">{t.type.replace(/_/g, ' ')}</p>
                                )}
                              </div>
                              <Plus size={14} className="text-blue-600 shrink-0" />
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* ── Unavailable trucks ── */}
                  {busyTrucks.length > 0 && (
                    <div className="mb-3">
                      <p className="text-[11px] font-bold uppercase tracking-widest text-slate/60 mb-2">Unavailable (assigned elsewhere)</p>
                      <ul className="flex flex-col gap-1">
                        {busyTrucks.map(t => (
                          <li key={t.id}
                            className="flex items-center gap-3 px-3 py-2.5 rounded-xl border border-stone/50 opacity-50">
                            <div className="w-7 h-7 rounded-lg bg-stone/30 flex items-center justify-center shrink-0">
                              <Truck size={13} className="text-slate" />
                            </div>
                            <p className="text-[13px] text-slate truncate">{t.name}</p>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {availableTrucks.length === 0 && busyTrucks.length === 0 && !hasRecommendations && (
                    <div className="text-center py-8">
                      <Truck size={28} className="mx-auto text-slate/30 mb-2" />
                      <p className="text-[13px] text-slate">No trucks available</p>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>

        {/* Save button */}
        {!allSlotsFilled && assigned.length !== currentTrucks.length && (
          <button type="button" onClick={handleSave} disabled={saving}
            className="mt-3 w-full h-11 rounded-xl bg-plum text-white text-[14px] font-semibold
                       hover:bg-plum/90 disabled:opacity-50 transition-colors flex items-center justify-center gap-2">
            {saving ? <LoadingSpinner size="sm" /> : <Check size={16} />}
            {saving ? 'Saving\u2026' : 'Save Trucks'}
          </button>
        )}
      </div>
    </div>
  );
}
